// Copyright ©️ 2026 eVolve MEP, LLC

import type { DetailedHTMLProps, HTMLAttributes } from 'react';

import {
  ActionIcon,
  InputLabel,
  Overlay,
  Popover,
  type PopoverProps,
  Portal,
  LoadingOverlay,
  getDefaultZIndex,
} from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { focusAutoFocusElement } from 'components/Mantine/Modals/helpers';

import { PopoverConfirmation } from './PopoverConfirmation';
import type { PopoverContentProps } from './types';

const HiddenTarget = ({ ref, ...props }: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>) => (
  <div ref={ref} {...props} />
);

type PopoverMiddlewares = NonNullable<PopoverProps['middlewares']>;

const asOptions = <T,>(value: T | boolean | undefined): T | undefined =>
  typeof value === 'object' ? value : undefined;

/**
 * Merges consumer-provided middlewares over the built-in `shift`/`size` defaults per option,
 * so passing e.g. `{ shift: { crossAxis: true } }` doesn't clobber the defaults below.
 */
const mergeMiddlewares = (consumer: PopoverMiddlewares | undefined): PopoverMiddlewares => ({
  ...consumer,
  shift: consumer?.shift === false ? false : { boundary: 'clippingAncestors', ...asOptions(consumer?.shift) },
  // Cap the dropdown to the space available in the viewport so it can never
  // grow taller than the screen. Without this, `shift` pins the top edge and
  // the bottom of the popover (including the focused input and Save button)
  // slides off-screen — especially on mobile once the on-screen keyboard opens.
  size: consumer?.size === false ? false : { padding: 8, ...asOptions(consumer?.size) },
});

type Props = Omit<PopoverProps, 'children' | 'onClose'> &
  PopoverContentProps & {
    onClickClose: () => void;
    jiggling: boolean;
    setJiggling: (jiggling: boolean) => void;
  };

/**
 * The desktop dropdown for a `WrappedPopover`.
 *
 * @remarks Do not use directly - use WrappedPopover instead
 */
export const DesktopPopover = ({
  opened,
  onClose,
  onClickClose,
  onConfirm,
  withForm,
  disabled,
  loading,
  width,
  target,
  confirmationText,
  confirmationCount,
  withCloseButton,
  title,
  withinPortal,
  children,
  jiggling,
  setJiggling,
  middlewares: consumerMiddlewares,
  ...props
}: Props) => (
  <>
    {opened && (
      // When the user needs to take an action (because the form is dirty)
      // prevent them from clicking anywhere else on the screen
      <Portal>
        <Overlay
          onClick={(e) => {
            onClickClose();
            e.stopPropagation();
          }}
          fixed
          opacity={0}
        />
      </Portal>
    )}
    <Popover
      width={width}
      opened={opened}
      position="bottom-end"
      trapFocus
      // closeOnClickOutside does not work when using a controlled Popover,
      // hence the need for the `onClick` on the above `<Portal>`
      closeOnClickOutside
      onClose={onClickClose}
      middlewares={mergeMiddlewares(consumerMiddlewares)}
      transitionProps={{
        onEntered: () => focusAutoFocusElement('.mantine-Popover-dropdown'),
      }}
      keepMounted={false}
      hideDetached={false}
      withinPortal={withinPortal}
      {...props}
    >
      <Popover.Target>{target ?? <HiddenTarget />}</Popover.Target>
      <Popover.Dropdown
        className={jiggling ? 'jiggle' : ''}
        onAnimationEnd={() => setJiggling(false)}
        onClick={(e) => e.stopPropagation()}
        pb="md"
        // Lay the dropdown out as a column so the body can scroll while the footer stays pinned.
        style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {title && <InputLabel mb="xs">{title}</InputLabel>}
        {withCloseButton && (
          <ActionIcon
            color="gray"
            onClick={onClose}
            style={{ position: 'absolute', right: 5, top: 5, zIndex: getDefaultZIndex('popover') + 1 }}
          >
            <EvolveIcon icon="Close" size="sm" color="dark" />
          </ActionIcon>
        )}
        <PopoverConfirmation
          onConfirm={onConfirm}
          onClose={onClose}
          withForm={withForm}
          disabled={disabled}
          confirmationText={confirmationText}
          confirmationCount={confirmationCount}
        >
          {children}
        </PopoverConfirmation>
        <LoadingOverlay visible={loading} />
      </Popover.Dropdown>
    </Popover>
  </>
);
